import { useEffect, useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import { useGovernanceOptional } from './GovernanceProvider'
import { searchCommands, type CommandItem } from './governanceSearch'
import RolePermissionPanel from './RolePermissionPanel'
import { GOV_ROLES, effectiveRole, type GovRole } from './governanceModel'

export interface OliviumCommandBarProps {
  onOpenPath?: (to: string) => void
}

const chip: CSSProperties = {
  fontSize: 12, padding: '5px 9px', borderRadius: 'var(--sc-radius-sm)',
  border: '1px solid var(--govv-line)', background: 'var(--sc-surface-2)', color: 'var(--govv-ink-2)',
  cursor: 'pointer', whiteSpace: 'nowrap',
}

const KIND_LABEL: Record<CommandItem['kind'], string> = {
  navigate: 'Go', create: 'Create', role: 'Role', record: 'Record', reset: 'Reset',
}

/**
 * OliviumCommandBar — the suite's command palette + global search + create menu
 * + test-as-role, mounted in the shell header. Self-guards with the optional
 * governance hook, so the shell stays reusable outside a provider.
 */
export default function OliviumCommandBar({ onOpenPath }: OliviumCommandBarProps) {
  const gov = useGovernanceOptional()
  const [open, setOpen] = useState(false)
  const [rolesOpen, setRolesOpen] = useState(false)
  const [query, setQuery] = useState('')

  useEffect(() => {
    if (!gov) return
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setOpen((v) => !v) }
      if (e.key === 'Escape') { setOpen(false); setRolesOpen(false) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [gov])

  const results = useMemo(() => (gov ? searchCommands(gov.state, query) : []), [gov, query])

  if (!gov) return null
  const role = effectiveRole(gov.state)

  const choose = (item: CommandItem) => {
    setOpen(false); setQuery('')
    if (item.kind === 'role') { gov.run({ type: 'set_viewer_role', role: item.role ?? null }); return }
    if (item.path && onOpenPath) onOpenPath(item.path)
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <button type="button" style={chip} onClick={() => setOpen(true)} aria-label="Open command palette (Command K)" title="Command palette · ⌘K">
        Search · <kbd style={{ fontFamily: 'var(--sc-font-mono)', fontSize: 11 }}>⌘K</kbd>
      </button>
      <button type="button" style={chip} onClick={() => { setQuery('Create '); setOpen(true) }} aria-label="Create">+ Create</button>
      <button type="button" style={chip} onClick={() => setRolesOpen(true)} aria-label="Roles and permissions">Roles</button>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <span style={{ fontSize: 10, color: 'var(--govv-ink-3)', textTransform: 'uppercase' }}>As</span>
        <select
          aria-label="Test as role"
          value={gov.state.viewerRole ?? '__actual__'}
          onChange={(e) => gov.run({ type: 'set_viewer_role', role: e.target.value === '__actual__' ? null : (e.target.value as GovRole) })}
          style={{ ...chip, cursor: 'pointer' }}
        >
          <option value="__actual__">{gov.state.actor.role} (actual)</option>
          {GOV_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </label>

      {open && (
        <div className="olv-gov-modal-backdrop" onClick={() => setOpen(false)} role="presentation">
          <div className="olv-gov-modal olv-gov" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="Command palette" style={{ maxWidth: 620, padding: 0 }}>
            <input
              autoFocus
              aria-label="Search records and commands"
              placeholder="Search records, go to a module, create, or test a role…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ width: '100%', padding: '14px 16px', fontSize: 15, border: 'none', borderBottom: '1px solid var(--govv-line)', background: 'transparent', color: 'var(--govv-ink)', outline: 'none' }}
            />
            <div style={{ maxHeight: '52vh', overflowY: 'auto' }}>
              {results.length === 0 && (
                <p style={{ padding: 18, margin: 0, color: 'var(--govv-ink-3)', fontSize: 13 }}>No matches. Try a module name, a record title, or “Create”.</p>
              )}
              {results.slice(0, 40).map((item) => (
                <button key={item.id} type="button" className="olv-gov-row" onClick={() => choose(item)}
                  style={{ display: 'flex', width: '100%', textAlign: 'left', gap: 10, alignItems: 'center', padding: '10px 16px', border: 'none', background: 'transparent', cursor: 'pointer', borderBottom: '1px solid var(--govv-line)' }}>
                  <span className="olv-gov-pill" style={{ color: 'var(--govv-structure)' }}>{KIND_LABEL[item.kind]}</span>
                  <span style={{ flex: 1, fontSize: 13, color: 'var(--govv-ink)' }}>{item.label}</span>
                  <span style={{ fontSize: 11, color: 'var(--govv-ink-3)' }}>{item.hint}</span>
                </button>
              ))}
            </div>
            <div style={{ padding: '8px 16px', fontSize: 11, color: 'var(--govv-ink-3)', borderTop: '1px solid var(--govv-line)' }}>
              Acting as {role}. Everything here operates the local-session governance ledger.
            </div>
          </div>
        </div>
      )}

      {rolesOpen && <RolePermissionPanel onClose={() => setRolesOpen(false)} />}
    </div>
  )
}
